// src/game/engine/gameMachine.js

export function createGameMachine(initialContext = {}) {
  let currentState = "idle";
  const {
    consumeAction,
    playPunchSound,
    playPopSound,
    playDodgeSound,
    playBeaten,
  } = initialContext;
  const base = process.env.PUBLIC_URL || "";
  let context = {
    leftArmImage: `${base}/assets/img/l_loose.png`,
    rightArmImage: `${base}/assets/img/r_loose.png`,
    punchSequence: [],
    currentPunchIndex: 0,
    comboSet: { primary: [], mirrored: null }, // To hold the generated combos
    currentComboName: "primary", // "primary" or "mirrored"
    currentCombo: null, // Holds the current combo array
    dodgeAnticipation: false, // new state for dodge anticipation
    characterX: 0.5, // Player's character x-position
    gloveX: 0.5, // Opponent's glove x-position
  };

  const listeners = new Set();

  const setCombosAction = (payload) => {
    const firstCombo =
      payload.primary && payload.primary.length > 0 ? payload.primary[0] : null;

    context = {
      ...context,
      comboSet: payload,
      currentCombo: firstCombo,
      currentPunchIndex: 0,
      punchSequence: [], // Clear old sequence
    };

    if (playPopSound) {
      playPopSound();
    }
  };

  const machine = {
    idle: {
      on: {
        START: () => {
          // This logic ensures that if combos are set, the first one is ready.
          if (
            !context.currentCombo &&
            context.comboSet.primary &&
            context.comboSet.primary.length > 0
          ) {
            context = {
              ...context,
              currentCombo: context.comboSet.primary[0],
            };
          }
          return "playing";
        },
        SET_COMBOS: (payload) => {
          setCombosAction(payload);
          return "idle";
        },
      },
    },
    playing: {
      on: {
        SET_COMBOS: (payload) => {
          setCombosAction(payload);
          return "playing";
        },
        SET_NEXT_COMBO: () => {
          if (!context.comboSet || !context.comboSet.primary.length) {
            console.warn("No combos set in game machine.");
            return "playing";
          }

          let nextComboName = context.currentComboName;
          if (context.comboSet.mirrored) {
            nextComboName =
              context.currentComboName === "primary" ? "mirrored" : "primary";
          }

          const nextPunchSequence = context.comboSet[nextComboName];
          const nextCombo = nextPunchSequence;

          context = {
            ...context,
            punchSequence: nextPunchSequence,
            currentCombo: nextCombo,
            currentPunchIndex: 0,
            currentComboName: nextComboName,
          };

          if (playPopSound && context.punchSequence.length > 0) {
            playPopSound();
          }

          notify();
          return "playing";
        },
        POSE_UPDATE: ({ poses, actions, position }) => {
          let hasChanged = false;

          // Update character and glove positions during dodge states
          if (
            (currentState === "playing" && context.dodgeAnticipation) ||
            (currentState === "playing" &&
              context.currentCombo &&
              context.currentCombo[context.currentPunchIndex]?.type === "dodge")
          ) {
            if (position && typeof position.x === "number") {
              const invertedX = 1 - position.x;
              // Update characterX during both DODGE_START and DODGE_IMPACT
              context = { ...context, characterX: invertedX };
              hasChanged = true;

              // Update gloveX only during DODGE_START (dodgeAnticipation is true)
              if (context.dodgeAnticipation) {
                context = { ...context, gloveX: invertedX };
              }
            }
          } else {
            // If not dodging, and character is not centered, reset it.
            if (context.characterX !== 0.5) {
              context = { ...context, characterX: 0.5 };
              hasChanged = true;
            }
          }

          // Add hitbox debug info during dodge
          if (
            context.currentCombo &&
            context.currentCombo[context.currentPunchIndex]?.type === "dodge"
          ) {
            const expectedMove =
              context.currentCombo[context.currentPunchIndex];
            const headWidth = 0.1;
            const gloveWidth = 0.1;
            const headX = context.characterX;
            const gloveX = context.gloveX;

            const headMin = headX - headWidth / 2;
            const headMax = headX + headWidth / 2;
            const gloveMin = gloveX - gloveWidth / 2;
            const gloveMax = gloveX + gloveWidth / 2;

            const isHit = headMin < gloveMax && headMax > gloveMin;

            context = {
              ...context,
              hitboxDebugInfo: {
                head: { min: headMin, max: headMax },
                glove: { min: gloveMin, max: gloveMax },
                isHit,
              },
            };
            hasChanged = true;
          } else {
            // Clear hitbox info when not dodging
            if (context.hitboxDebugInfo) {
              context = { ...context, hitboxDebugInfo: null };
              hasChanged = true;
            }
          }

          // Arm image update logic
          let newLeftArmImage = `${base}/assets/img/l_loose.png`;
          if (poses.left.straight)
            newLeftArmImage = `${base}/assets/img/l_straight.png`;
          else if (poses.left.block)
            newLeftArmImage = `${base}/assets/img/l_block.png`;

          let newRightArmImage = `${base}/assets/img/r_loose.png`;
          if (poses.right.straight)
            newRightArmImage = `${base}/assets/img/r_straight.png`;
          else if (poses.right.block)
            newRightArmImage = `${base}/assets/img/r_block.png`;

          if (
            newLeftArmImage !== context.leftArmImage ||
            newRightArmImage !== context.rightArmImage
          ) {
            context = {
              ...context,
              leftArmImage: newLeftArmImage,
              rightArmImage: newRightArmImage,
            };
            hasChanged = true;
          }

          // Punch detection logic
          const punchedSide = actions.left.punched
            ? "left"
            : actions.right.punched
            ? "right"
            : null;

          if (punchedSide) {
            consumeAction(punchedSide);

            const expectedMove =
              context.punchSequence[context.currentPunchIndex];

            if (
              expectedMove &&
              expectedMove.type === "punch" &&
              punchedSide === expectedMove.side
            ) {
              console.log("punchedSide", punchedSide);

              // Play punch sound for successful combo punch
              if (playPunchSound) {
                playPunchSound(punchedSide);
              }

              const newIndex = context.currentPunchIndex + 1;
              context = { ...context, currentPunchIndex: newIndex };

              // If combo is complete, clear the sequence
              if (newIndex >= context.punchSequence.length) {
                context = { ...context, punchSequence: [] };
              }
              hasChanged = true;
            }
          }

          if (hasChanged) notify();
          return "playing";
        },
        DODGE_START: () => {
          console.log("Doge");
          context = { ...context, dodgeAnticipation: true };
          notify();
          return "playing";
        },
        DODGE_IMPACT: () => {
          context = {
            ...context,
            dodgeAnticipation: false,
          };
          notify();
          return "playing";
        },
        DODGE_SUCCESS: () => {
          const expectedMove = context.punchSequence[context.currentPunchIndex];
          if (expectedMove && expectedMove.type === "dodge") {
            // Check for collision
            const headWidth = 0.1; // 10% of viewport
            const gloveWidth = 0.1; // 10% of viewport
            const headX = context.characterX;
            const gloveX =
              expectedMove.side === "left"
                ? 1 - context.gloveX
                : context.gloveX;

            const headMin = headX - headWidth / 2;
            const headMax = headX + headWidth / 2;
            const gloveMin = gloveX - gloveWidth / 2;
            const gloveMax = gloveX + gloveWidth / 2;

            const isHit = headMin < gloveMax && headMax > gloveMin;

            if (isHit) {
              if (playBeaten) playBeaten();
            } else {
              if (playDodgeSound) playDodgeSound();
            }

            const newIndex = context.currentPunchIndex + 1;
            context = { ...context, currentPunchIndex: newIndex };
            if (newIndex >= context.punchSequence.length) {
              context = { ...context, punchSequence: [] };
            }
            notify();
          }
          return "playing";
        },
      },
    },
    complete: {},
  };

  function send(event, payload) {
    const currentStateDefinition = machine[currentState];
    if (!currentStateDefinition || !currentStateDefinition.on) return;

    const transition = currentStateDefinition.on[event];
    if (!transition) return;

    let nextState;
    if (typeof transition === "string") {
      nextState = transition;
    } else if (typeof transition === "function") {
      nextState = transition(payload);
    }

    if (nextState && machine[nextState]) {
      const previousState = currentState;
      currentState = nextState;
      if (
        previousState !== currentState ||
        event === "SET_NEXT_COMBO" ||
        event === "DODGE_START" ||
        event === "DODGE_IMPACT" ||
        event === "DODGE_SUCCESS" ||
        event === "SET_COMBOS" || // Notify on new combos
        (event === "POSE_UPDATE" && payload.actions.left.punched) ||
        (event === "POSE_UPDATE" && payload.actions.right.punched)
      ) {
        notify();
      }
    }
  }

  function subscribe(callback) {
    listeners.add(callback);
    callback({ state: currentState, context });
    return () => listeners.delete(callback);
  }

  function notify() {
    for (const listener of listeners) {
      listener({ state: currentState, context });
    }
  }

  return { send, subscribe };
}
